import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export type SettingSection =
  | "llm"
  | "database"
  | "ibkr"
  | "data_sources"
  | "feature_flags";

export type SettingValueType =
  | "str"
  | "int"
  | "float"
  | "decimal"
  | "bool"
  | "secret";

export type SettingValue = string | number | boolean | null;

export interface AdminSetting {
  key: string;
  section: SettingSection;
  value_type: SettingValueType;
  description: string | null;
  is_secret: boolean;
  requires_restart: boolean;
  value: SettingValue;
  has_value: boolean;
  updated_at: string | null;
  updated_by_user_id: string | null;
  updated_by_display_name: string | null;
}

export interface UpdateSettingBody {
  value: SettingValue;
}

export const adminSettingsKey = ["admin", "settings"] as const;

export function useAdminSettingsList() {
  return useQuery<AdminSetting[]>({
    queryKey: adminSettingsKey,
    queryFn: () => apiFetch<AdminSetting[]>("/admin/settings"),
    staleTime: 30_000,
  });
}

interface UpdateContext {
  previous: AdminSetting[] | undefined;
}

/**
 * PATCH /admin/settings/{key} with optimistic UI: the row is patched
 * locally before the request completes; on error the snapshot is
 * restored; on success the server response replaces the row.
 */
export function useUpdateSetting(key: string) {
  const qc = useQueryClient();
  return useMutation<AdminSetting, Error, UpdateSettingBody, UpdateContext>({
    mutationFn: (body) =>
      apiFetch<AdminSetting>(`/admin/settings/${encodeURIComponent(key)}`, {
        method: "PATCH",
        body,
      }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: adminSettingsKey });
      const previous = qc.getQueryData<AdminSetting[]>(adminSettingsKey);
      if (previous) {
        qc.setQueryData<AdminSetting[]>(adminSettingsKey, (rows) =>
          (rows ?? []).map((row) =>
            row.key === key
              ? {
                  ...row,
                  // Secrets stay masked locally too — never write plaintext into cache.
                  value: row.is_secret ? null : vars.value,
                  has_value: row.is_secret
                    ? row.has_value || vars.value !== null
                    : vars.value !== null,
                  updated_at: new Date().toISOString(),
                }
              : row,
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(adminSettingsKey, ctx.previous);
      }
    },
    onSuccess: (updated) => {
      qc.setQueryData<AdminSetting[]>(adminSettingsKey, (rows) =>
        (rows ?? []).map((row) => (row.key === updated.key ? updated : row)),
      );
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: adminSettingsKey });
    },
  });
}
